import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';
import API_BASE_URL, { API_ENDPOINTS, apiPost } from '../config/api';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-color) 0%, #e67e00 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const LogoSection = styled.div`
  margin-bottom: 30px;
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: var(--white-color);
`;

const LogoTitle = styled.h1`
  font-size: 1.8rem;
  color: var(--primary-color);
  margin-bottom: 5px;
  font-weight: 700;
`;

const LogoSubtitle = styled.p`
  color: var(--secondary-color);
  font-size: 0.9rem;
  opacity: 0.8;
`;

const LoginForm = styled.form`
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--secondary-color);
  font-weight: 600;
  font-size: 0.9rem;
`;

const InputContainer = styled.div`
  position: relative;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 15px 15px 15px 45px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.1);
  }
  
  &.error {
    border-color: #e74c3c;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--secondary-color);
  opacity: 0.6;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--secondary-color);
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const RememberMeSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  font-size: 0.9rem;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--secondary-color);
  cursor: pointer;
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
  }
`;

const ForgotPassword = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  
  &:hover {
    color: #e67e00;
  }
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  background: var(--primary-color);
  color: var(--white-color);
  border: none;
  padding: 15px;
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 20px;
  
  &:hover {
    background: #e67e00;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: var(--light-gray);
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: var(--border-radius);
  margin-bottom: 20px;
  font-size: 0.9rem;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px;
  border-radius: var(--border-radius);
  margin-bottom: 20px;
  font-size: 0.9rem;
  border: 1px solid #c3e6cb;
`;

const SecurityNote = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 15px;
  border-radius: var(--border-radius);
  font-size: 0.8rem;
  border: 1px solid #ffeaa7;
  margin-top: 20px;
  
  .security-icon {
    color: var(--primary-color);
    margin-right: 8px;
  }
`;

const LoginFormComponent = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
    rememberMe: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Hata mesajını temizle
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.usernameOrEmail || !formData.password) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const data = await apiPost(API_ENDPOINTS.LOGIN, {
        usernameOrEmail: formData.usernameOrEmail.trim(),
        password: formData.password
      });
      setSuccess('Giriş başarılı! Yönlendiriliyorsunuz...');

      const loginPayload = {
        id: data.user?.id, // id bilgisini ekle
        username: data.user?.username || formData.usernameOrEmail,
        email: data.user?.email,
        role: data.user?.role,
        branch: data.user?.branch,
        rememberMe: formData.rememberMe
      };

      if (formData.rememberMe) {
        localStorage.setItem('sultanlarAdmin', JSON.stringify(loginPayload));
      }

      setTimeout(() => {
        onLogin(loginPayload);
      }, 800);
    } catch (err) {
      // Daha anlaşılır hata mesajları
      let errorMessage = 'Giriş başarısız';
      
      if (err?.message) {
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage = `API sunucusuna bağlanılamıyor. Adres: ${API_BASE_URL}`;
        } else if (err.message.includes('404') || err.message.includes('Endpoint bulunamadı')) {
          errorMessage = `Giriş adresi bulunamadı (404). API kök adresini kontrol edin: ${API_BASE_URL}`;
        } else if (err.message.includes('401') || err.message.includes('hatalı')) {
          errorMessage = 'Kullanıcı adı veya şifre hatalı';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      console.error('Giriş hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginCard
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <LogoSection>
          <LogoIcon>
            <FaShieldAlt />
          </LogoIcon>
          <LogoTitle>SULTANLAR BESİCİLİK</LogoTitle>
          <LogoSubtitle>Personel Paneli</LogoSubtitle>
        </LogoSection>

        <LoginForm onSubmit={handleSubmit}>
          {error && <ErrorMessage>{typeof error === 'string' ? error : JSON.stringify(error)}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormGroup>
            <FormLabel>Kullanıcı Adı veya E-posta</FormLabel>
            <InputContainer>
              <InputIcon>
                <FaUser />
              </InputIcon>
              <FormInput
                type="text"
                name="usernameOrEmail"
                placeholder="Kullanıcı adı veya e-posta"
                value={formData.usernameOrEmail}
                onChange={handleInputChange}
                className={error && !formData.usernameOrEmail ? 'error' : ''}
                autoComplete="username"
              />
            </InputContainer>
          </FormGroup>

          <FormGroup>
            <FormLabel>Şifre</FormLabel>
            <InputContainer>
              <InputIcon>
                <FaLock />
              </InputIcon>
              <FormInput
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Şifrenizi girin"
                value={formData.password}
                onChange={handleInputChange}
                className={error && !formData.password ? 'error' : ''}
                autoComplete="current-password"
              />
              <PasswordToggle
                type="button"
                onClick={togglePasswordVisibility}
                title={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </PasswordToggle>
            </InputContainer>
          </FormGroup>

          <RememberMeSection>
            <CheckboxContainer>
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
              />
              <span>Beni hatırla</span>
            </CheckboxContainer>
            <ForgotPassword type="button">
              Şifremi unuttum
            </ForgotPassword>
          </RememberMeSection>

          <LoginButton
            type="submit"
            disabled={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </LoginButton>
        </LoginForm>

        <SecurityNote>
          <span className="security-icon">🔒</span>
          <strong>Güvenlik Notu:</strong> Bu panel sadece yetkili personel tarafından kullanılmalıdır. 
          Giriş bilgilerinizi kimseyle paylaşmayın.
        </SecurityNote>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginFormComponent; 